import { CopyButton } from "@expert/common-ui";
import { CopyIcon } from "@expert/common-ui/assets";
import { useCustomerAvatar } from "@expert/contentful";
import { Button, CopyButton as Copy, Group, Image, Stack, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";

import { useAnalytics } from "@/analytics";
import { PhoneNumber, ProperName } from "@/common-ui";
import { useVerifiedCustomerStore } from "@/customer-info";
import { useCallIntroStore } from "@/customer-panel/components/call-intro-flow/store/CallIntroStore";
import { useVerificationFlowStore } from "@/customer-panel/components/customer-verification-store";
import { useProductOfferStore } from "@/sales-tools";
import { clearEligibilityQueryCache } from "@/sales-tools/home-product/hooks/useEligibility";
import { useIntegratedFlowStore } from "@/sales-tools/home-product/stores/useIntegratedFlowStore";
import { SaleProgress } from "@/sales-tools/home-product/types";
import { useSessionId } from "@/sdk";
import { removeCountryCode } from "@/shared-utils";

import classes from "./CustomerProfile.module.css";
import { EditIcon } from "./EditIcon";

export function CustomerProfile() {
    const sessionId = useSessionId();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const stepHistoryUpdated = useVerificationFlowStore((state) => state.getStepHistory(sessionId));
    const callIntroStep = useCallIntroStore((state) => state.currentStep);
    const customerAvatarUrl = useCustomerAvatar(sessionId);
    const products = useProductOfferStore((state) => state.getProducts(sessionId));
    const hasDispositionedAnyProduct = products.some((product) => product.saleProgress !== SaleProgress.InProgress);
    const queryClient = useQueryClient();
    const { dispatcher } = useAnalytics();

    const isVerificationComplete =
        stepHistoryUpdated[stepHistoryUpdated.length - 1] === "VerificationComplete" ||
        callIntroStep === "IntroFlowCompleted";
    const shouldShowEditButton =
        isVerificationComplete && !hasDispositionedAnyProduct && callIntroStep !== "IntroFlowCompleted";

    const handleEditClick = () => {
        void dispatcher.dispatchBusinessEvent("EditCustomerInfoClicked");
        clearEligibilityQueryCache(queryClient, sessionId);
        useVerificationFlowStore.getState().pushStep(sessionId, "CustomerPhoneInput");
        useCallIntroStore.getState().pushStep("CustomerInfoCollection");
        useIntegratedFlowStore.getState().resetIntegratedStepStore(sessionId);
        useProductOfferStore.getState().resetSaleSession(sessionId);
    };

    return (
        <Group justify={isVerificationComplete ? "space-between" : "center"} gap="0" align="top" wrap="nowrap">
            <Group gap="0" wrap="nowrap">
                {customerAvatarUrl !== undefined && (
                    <Image className={classes.profilePicture} data-testid="customer-avatar" src={customerAvatarUrl} />
                )}
                {isVerificationComplete && (
                    <Stack gap="0">
                        <ProperName
                            data-testid="customer-name"
                            size="sm"
                            truncate
                            name={customer?.fullName}
                            className={`${classes.name} fs-mask`}
                        />
                        {customer?.mdn ? (
                            <Group gap={4} wrap="nowrap">
                                <Text w="max-content" className={`${classes.customerIdentifier} fs-mask`} size="sm">
                                    <PhoneNumber mdn={customer.mdn} />
                                </Text>
                                <CopyButton value={removeCountryCode(customer.mdn)} />
                            </Group>
                        ) : (
                            customer?.email && (
                                <Group gap="0">
                                    <Text
                                        w="max-content"
                                        className={`${classes.customerIdentifier} fs-mask data-testid="customer-email"`}
                                        size="xs"
                                        data-testid="customer-email"
                                    >
                                        {customer.email}
                                    </Text>
                                </Group>
                            )
                        )}
                        {customer?.metadata?.caseNumber && (
                            <Copy value={customer?.metadata?.caseNumber}>
                                {({ copied, copy }) => (
                                    <Group onClick={copy} gap={5} style={{ cursor: "pointer" }}>
                                        <Text
                                            w="max-content"
                                            c="gray.6"
                                            className={`fs-mask data-testid="customer-case-number"`}
                                            size="sm"
                                            data-testid="customer-case-number"
                                        >
                                            {copied ? "Copied" : `Case #${customer?.metadata?.caseNumber}`}
                                        </Text>
                                        <Group gap={0}>
                                            <CopyIcon width="12" stroke="var(--mantine-color-gray-6)" />
                                        </Group>
                                    </Group>
                                )}
                            </Copy>
                        )}
                    </Stack>
                )}
            </Group>

            {shouldShowEditButton && (
                <Button
                    id="mdn-edit-button"
                    onClick={handleEditClick}
                    color="dark.6"
                    size="xs"
                    variant="default"
                    data-testid="customer-profile-edit-button"
                    px={"xs"}
                >
                    <EditIcon />
                </Button>
            )}
        </Group>
    );
}
